import type { VoidFunctionComponent } from 'react';
import * as _ from 'lodash';
import type Stripe from 'stripe-latest';
import classNames from 'classnames';
import { dynamicPlanFeatures, getPlanLabel, planIcon } from '../utils';
import type { StripePriceLimitType } from '../../../../../shared/model-typings/stripepricelimits';
import Icon, { GreenCheck, icons } from '../../../generic/Icon';
import { ICON_SIZE } from '../../../generic/Icon/icon-constants';
import Tooltip from '../../../generic/Tooltip';
import {
  featureDescriptions,
} from '../../billing/features/consts';
import type {
  FeatureGroups,
  FullFeatureDescriptions,
  FullTierDescription,
} from '../../billing/features/consts';
import Accordion from '../../../generic/Accordion';
import Flex from '../../../generic/Flex';
import styles from './styles.module.less';
import { usePlansContext } from './PlansContext';
import Loader from '../../../generic/Loader';
import { DisplayBusinessPricesSlider } from './DisplayBusinessPlan';

type FeatureTableProps = {
  title: string,
  featureDetails: FullFeatureDescriptions[FeatureGroups],
  freeFeatures: FullTierDescription[FeatureGroups],
  businessFeatures: FullTierDescription[FeatureGroups],
  enterpriseFeatures: FullTierDescription[FeatureGroups],
  currentPlanType: StripePriceLimitType,
};

type FeatureDetailProps = {
  value: string | boolean,
  isActive: boolean,
};
const FeatureDetail: VoidFunctionComponent<FeatureDetailProps> = ({
  value,
  isActive,
}) => {
  const featureDetailsClassName = classNames(
    styles.featureDetailsValue,
    { [styles.featureDetailsValueActive]: isActive },
  );

  if (_.isString(value)) {
    return <span className={featureDetailsClassName}>{value}</span>;
  }

  if (value) {
    return (
      <span className={featureDetailsClassName}>
        <GreenCheck size={ICON_SIZE.LG} />
      </span>
    );
  }

  return (
    <span className={featureDetailsClassName}>
      <span className={styles.featureDisabled}>
        <Icon icon={icons.times} size={ICON_SIZE.LG} />
      </span>
    </span>
  );
};

const FeatureTable: VoidFunctionComponent<FeatureTableProps> = ({
  title,
  featureDetails,
  freeFeatures,
  businessFeatures,
  enterpriseFeatures,
  currentPlanType,
}) => {
  const rows = Object.keys(featureDetails)
    .map((key: string) => (
      <div className={styles.featureRow} key={key}>
        <Flex justify="flex-start" gap={8}>
          {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
          {featureDetails[key].name}
          {/* @ts-expect-error We know that this will exist because of the strict typings of the feature objects */}
          {featureDetails[key]?.tooltipText && (
            // @ts-expect-error We know that this will exist because of the strict typings of the feature objects
            <Tooltip tooltip={featureDetails[key].tooltipText} id={key}>
              <span className={styles.featureRowTooltip}>
                <Icon icon={icons.questionCircle} />
              </span>
            </Tooltip>
          )}
        </Flex>
        <FeatureDetail
          value={freeFeatures[key as keyof FullTierDescription[FeatureGroups]]}
          isActive={currentPlanType === 'FREE'}
        />
        <FeatureDetail
          value={businessFeatures[key as keyof FullTierDescription[FeatureGroups]]}
          isActive={currentPlanType === 'BUSINESS'}
        />
        <FeatureDetail
          value={enterpriseFeatures[key as keyof FullTierDescription[FeatureGroups]]}
          isActive={currentPlanType === 'ENTERPRISE'}
        />
      </div>
    ));

  return (
    <div className={styles.featureAccordion}>
      <Accordion key={title} id={title} isInitiallyOpen={title === 'Main Features'}>
        <Accordion.Target>
          {title}
        </Accordion.Target>
        <Accordion.Body>
          {rows}
        </Accordion.Body>
      </Accordion>
    </div>
  );
};

type FeatureComparisonHeaderProps = {
  currentPlanType: StripePriceLimitType,
};
const FeatureComparisonHeader: VoidFunctionComponent<FeatureComparisonHeaderProps> = ({
  currentPlanType,
}) => {
  const { plans } = usePlansContext();
  return (
    <div className={styles.featureComparisonContainer}>
      <div className={styles.featureComparisonTitle}>
        Feature Comparison
      </div>
      {plans && plans.map((plan) => {
        const isBusiness = plan.type === 'BUSINESS';
        const price = plan.prices[0] as Stripe.Price;
        const product = price.product as Stripe.Product;
        const isActive = currentPlanType === product?.metadata?.type;
        return (
          <div key={product.name} className={isActive ? styles.featureComparisonHeaderActive : ''}>
            <Flex
              direction="column"
              gap={12}
              className={styles.featureComparisonHeaderItemContainer}
            >
              {isActive &&
                <div className={styles.featureComparisonCurrentPlan}>
                  Current Plan
                </div>}
              <Flex
                direction="row"
                align="center"
                gap={12}
                className={styles.featureComparisonHeader}
              >
                <img src={planIcon(product.metadata?.type as StripePriceLimitType)} alt={product.name} />
                {getPlanLabel(product)}
              </Flex>

              {isBusiness && <DisplayBusinessPricesSlider />}
            </Flex>
          </div>
        );
      })}
    </div>
  );
};

type FullFeatureTableProps = {
  currentPlanType: StripePriceLimitType,
};
const FullFeatureTable: VoidFunctionComponent<FullFeatureTableProps> = ({
  currentPlanType,
}) => {
  const {
    freePrice,
    enterprisePrice,
    selectedBusinessPrice,
    isLoading,
  } = usePlansContext();

  if (isLoading) {
    return (<Loader />);
  }

  if (!freePrice || !selectedBusinessPrice || !enterprisePrice) {
    return null;
  }

  const freeFeatureMap = dynamicPlanFeatures(freePrice);
  const businessFeatureMap = dynamicPlanFeatures(selectedBusinessPrice);
  const enterpriseFeatureMap = dynamicPlanFeatures(enterprisePrice);
  return (
    <>
      <FeatureComparisonHeader currentPlanType={currentPlanType} />

      <FeatureTable
        title="Main Features"
        featureDetails={featureDescriptions.mainFeatures}
        freeFeatures={freeFeatureMap.mainFeatures}
        businessFeatures={businessFeatureMap.mainFeatures}
        enterpriseFeatures={enterpriseFeatureMap.mainFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Code Scanning"
        featureDetails={featureDescriptions.codeScanningFeatures}
        freeFeatures={freeFeatureMap.codeScanningFeatures}
        businessFeatures={businessFeatureMap.codeScanningFeatures}
        enterpriseFeatures={enterpriseFeatureMap.codeScanningFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Security"
        featureDetails={featureDescriptions.securityFeatures}
        freeFeatures={freeFeatureMap.securityFeatures}
        businessFeatures={businessFeatureMap.securityFeatures}
        enterpriseFeatures={enterpriseFeatureMap.securityFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Compliance"
        featureDetails={featureDescriptions.complianceFeatures}
        freeFeatures={freeFeatureMap.complianceFeatures}
        businessFeatures={businessFeatureMap.complianceFeatures}
        enterpriseFeatures={enterpriseFeatureMap.complianceFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Quality"
        featureDetails={featureDescriptions.qualityFeatures}
        freeFeatures={freeFeatureMap.qualityFeatures}
        businessFeatures={businessFeatureMap.qualityFeatures}
        enterpriseFeatures={enterpriseFeatureMap.qualityFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="SBOM"
        featureDetails={featureDescriptions.sbomFeatures}
        freeFeatures={freeFeatureMap.sbomFeatures}
        businessFeatures={businessFeatureMap.sbomFeatures}
        enterpriseFeatures={enterpriseFeatureMap.sbomFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Reporting"
        featureDetails={featureDescriptions.reportingFeatures}
        freeFeatures={freeFeatureMap.reportingFeatures}
        businessFeatures={businessFeatureMap.reportingFeatures}
        enterpriseFeatures={enterpriseFeatureMap.reportingFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Admin Controls"
        featureDetails={featureDescriptions.adminControlFeatures}
        freeFeatures={freeFeatureMap.adminControlFeatures}
        businessFeatures={businessFeatureMap.adminControlFeatures}
        enterpriseFeatures={enterpriseFeatureMap.adminControlFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Customer Success and Technical Support"
        featureDetails={featureDescriptions.supportFeatures}
        freeFeatures={freeFeatureMap.supportFeatures}
        businessFeatures={businessFeatureMap.supportFeatures}
        enterpriseFeatures={enterpriseFeatureMap.supportFeatures}
        currentPlanType={currentPlanType}
      />
      <FeatureTable
        title="Deployment"
        featureDetails={featureDescriptions.deploymentFeatures}
        freeFeatures={freeFeatureMap.deploymentFeatures}
        businessFeatures={businessFeatureMap.deploymentFeatures}
        enterpriseFeatures={enterpriseFeatureMap.deploymentFeatures}
        currentPlanType={currentPlanType}
      />
    </>
  );
};

export default FullFeatureTable;
